import React from 'react';
import { NotificationDto, NotificationType } from '../../types/notifications';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    isReassignedAway,
    isStaffBookingNotificationType,
    staffBookingNotificationText,
    staffNotificationAppointmentId,
} from '../../lib/staffNotifications';
import styles from './NotificationPanel.module.css';

interface NotificationItemProps {
    notification: NotificationDto;
    onRead: (id: number) => void;
    onDelete: (id: number) => void;
    /**
     * S2 (AB#2205, SG-16) — open the booking a staff booking notification is
     * about. When absent, staff booking notifications only mark themselves read.
     */
    onOpenBooking?: (appointmentId: number) => void;
}

/** Fill `{name}` placeholders in a translated template. */
const fill = (template: string, values: Record<string, string | number>): string =>
    Object.entries(values).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), template);

const getRelativeTime = (
    dateString: string,
    t: (key: string) => string,
    language: string,
): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return t('onot.time.justNow');
    if (diffMins < 60) return fill(t('onot.time.minutesAgo'), { count: diffMins });
    if (diffHours < 24) return fill(t(diffHours === 1 ? 'onot.time.hourAgo' : 'onot.time.hoursAgo'), { count: diffHours });
    if (diffDays < 7) return fill(t(diffDays === 1 ? 'onot.time.dayAgo' : 'onot.time.daysAgo'), { count: diffDays });

    // O3 (SO-20) — day-first, never en-US.
    return date.toLocaleDateString(language === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short' });
};

// The API sends the enum name; older payloads sent the number — both handled.
const getNotificationIcon = (type: NotificationType | string): string => {
    switch (type) {
        case NotificationType.NewBusinessCreated:
        case 'NewBusinessCreated':
            return '🏪';
        case NotificationType.NewUserRegistered:
        case 'NewUserRegistered':
            return '👤';
        case NotificationType.NewAppointment:
        case 'NewAppointment':
        case 'StaffBookingAssigned':
        case 'StaffBookingChanged':
            return '📅';
        case NotificationType.AppointmentCancelled:
        case 'AppointmentCancelled':
        case 'StaffBookingCancelled':
            return '❌';
        case NotificationType.NewStaffJoined:
        case 'NewStaffJoined':
            return '👥';
        default:
            return '📢';
    }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
    notification,
    onRead,
    onDelete,
    onOpenBooking,
}) => {
    const { t, language } = useLanguage();

    // S2 (AB#2205, SG-16) — staff booking notifications: localized from type + data,
    // and a click opens the booking. Every other type keeps the server text.
    const isStaffBooking = isStaffBookingNotificationType(notification.type);
    const staffText = isStaffBooking
        ? staffBookingNotificationText(notification.type as Parameters<typeof staffBookingNotificationText>[0], notification.data, language, t)
        : null;
    // A booking reassigned to someone else is informational only (never opened).
    const bookingId = isStaffBooking && !isReassignedAway(notification.type, notification.data)
        ? staffNotificationAppointmentId(notification.data, notification.referenceId)
        : null;
    const title = staffText?.title ?? notification.title;
    const message = staffText ? staffText.message : notification.message;
    const opensBooking = bookingId != null && !!onOpenBooking;

    const handleClick = () => {
        if (!notification.isRead) {
            onRead(notification.id);
        }
        if (opensBooking) onOpenBooking!(bookingId!);
    };

    const ariaLabel = opensBooking
        ? fill(t('stf.notif.openNamed'), { title })
        : (notification.isRead ? undefined : fill(t('onot.bell.markReadNamed'), { title }));

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(notification.id);
    };

    return (
        <div
            className={`${styles.notificationItem} ${notification.isRead ? styles.read : styles.unread}`}
        >
            <div className={styles.notificationIcon} aria-hidden="true">
                {getNotificationIcon(notification.type)}
            </div>
            {/* O3 (SO-44) — the read action is a real button (was a clickable div). */}
            <button
                type="button"
                className={`${styles.notificationContent} ${styles.notificationContentButton}`}
                onClick={handleClick}
                aria-label={ariaLabel}
                data-testid={isStaffBooking ? 'staff-booking-notification' : undefined}
            >
                <span className={styles.notificationTitle}>{title}</span>
                {message && (
                    <span className={styles.notificationMessage}>{message}</span>
                )}
                <span className={styles.notificationTime}>{getRelativeTime(notification.createdAt, t, language)}</span>
            </button>
            <button
                type="button"
                className={styles.deleteButton}
                onClick={handleDelete}
                aria-label={t('onot.bell.delete')}
            >
                <span aria-hidden="true">✕</span>
            </button>
        </div>
    );
};

export default NotificationItem;
